import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { TenantFinderParent } from 'src/views/tenant-finder/categories'
import { Colors, Shapes } from 'src/components/theme'
import {
  AgriculturalIcon,
  EducationIcon,
  IndustrialIcon,
  MedicalIcon,
  MissingIcon,
  OfficeIcon,
  RetailIcon,
  SpecialPurposeIcon,
} from 'src/components/logo-avatars/property-type-icons'

export type LogoAvatarProps = {
  url: string | null
  alt: string
  size: number
  children?: React.ReactNode
  id?: string
  propertyType?: TenantFinderParent
  noBorder?: boolean
  outlineColor?: string
}

type ImageProps = {
  size?: number
  height?: number
  width?: number
  average?: boolean
}

const LogoWrapper = styled.div<ImageProps & { outlineColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: cover;
  width: ${(props: ImageProps) => props.size}px;
  min-width: ${(props: ImageProps) => props.size}px;
  max-width: ${(props: ImageProps) => props.size}px;
  height: ${(props: ImageProps) => props.size}px;
  min-height: ${(props: ImageProps) => props.size}px;
  max-height: ${(props: ImageProps) => props.size}px;
  overflow: hidden;
  border-radius: ${Shapes.borderRadius}px;
  border: solid 1px ${Colors.neutral100};
  ${(props) => props.outlineColor && `outline: solid 1px ${props.outlineColor};`}
  background-color: ${Colors.bgWhite};
`

const Logo = styled.img`
  max-width: 100%;
  object-fit: contain;
  padding: 1px;
  border-radius: ${Shapes.borderRadius}px;
`

const LogoIcon = styled.div<{ bg?: string; size: number; borderColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${(props) => props.size}px;
  width: ${(props) => props.size}px;
  padding: ${Shapes.spacing[1] / 2}px;
  background-color: ${(props) => props.bg || Colors.blueLight};
  border-radius: ${Shapes.borderRadius}px;
  ${(props) => props.borderColor && `border: solid 1px ${props.borderColor};`}
`

const isNull = (link: string) => link.slice(-4) === 'null'

export const LogoAvatar = (props: LogoAvatarProps) => {
  const { url, alt, size, propertyType, noBorder = false, outlineColor } = props
  // this is to avoid chopping off the top and bottom of the AlphaMap logo since it's not QUITE square
  const calculatedSize = url === '/logo.svg' ? size + size * 0.03 : size

  const propertyTypeLogo = useMemo(() => {
    if (!propertyType) {
      return null
    }
    switch (propertyType) {
      case 'Agricultural':
        return {
          src: <AgriculturalIcon size={size * 0.5} fill={Colors.blueDark} />,
          alt: 'Agricultural',
        }
      case 'Education':
        return {
          src: <EducationIcon size={size * 0.5} fill={Colors.blueDark} />,
          alt: 'Education',
        }
      case 'Industrial':
        return {
          src: <IndustrialIcon size={size * 0.5} fill={Colors.blueDark} />,
          alt: 'Industrial',
        }
      case 'Medical':
        return {
          src: <MedicalIcon size={size * 0.5} fill={Colors.blueDark} />,
          alt: 'Medical',
        }
      case 'Office':
        return {
          src: <OfficeIcon size={size * 0.5} fill={Colors.blueDark} />,
          alt: 'Office',
        }
      case 'Retail':
        return {
          src: <RetailIcon size={size * 0.5} fill={Colors.blueDark} />,
          alt: 'Retail',
        }
      case 'Special Purpose':
        return {
          src: <SpecialPurposeIcon size={size * 0.5} fill={Colors.blueDark} />,
          alt: 'Special Purpose',
        }
      default:
        return null
    }
  }, [propertyType])

  // #1 - Try logo url
  if (url && url !== null && !isNull(url)) {
    return (
      <LogoWrapper
        size={calculatedSize}
        width={size}
        style={noBorder ? { border: 0, borderRadius: 0 } : undefined}
        outlineColor={outlineColor}>
        <Logo src={url} alt={alt} width={`${size}`} height="auto" />
      </LogoWrapper>
    )
  }

  // #2 - Try property type logo
  if (propertyTypeLogo) {
    return <LogoIcon size={size}>{propertyTypeLogo.src}</LogoIcon>
  }

  // #3 - Try missing logo
  return (
    <LogoIcon size={size}>
      <MissingIcon size={size} fill={Colors.blueDark} />
    </LogoIcon>
  )
}
